import { useCallback, useEffect, useRef, useState } from "react";
import { readMediaMetadata } from "../../core/media/readMediaMetadata";
import type { MediaImportState } from "../../core/media/mediaTypes";
import { validateMediaFile } from "../../core/media/validateMediaFile";
import { AppError } from "../../shared/errors/AppError";

const INITIAL_STATE: MediaImportState = { status: "idle", asset: null, error: null };

export function useMediaProject() {
  const [state, setState] = useState<MediaImportState>(INITIAL_STATE);
  const activeObjectUrl = useRef<string | null>(null);

  const releaseObjectUrl = useCallback(() => {
    if (!activeObjectUrl.current) return;
    URL.revokeObjectURL(activeObjectUrl.current);
    activeObjectUrl.current = null;
  }, []);

  useEffect(() => releaseObjectUrl, [releaseObjectUrl]);

  const importFile = useCallback(async (file: File) => {
    releaseObjectUrl();
    setState({ status: "loading", asset: null, error: null });

    let objectUrl: string | null = null;

    try {
      const kind = validateMediaFile(file);
      objectUrl = URL.createObjectURL(file);
      activeObjectUrl.current = objectUrl;
      const asset = await readMediaMetadata(file, kind, objectUrl);
      setState({ status: "ready", asset, error: null });
    } catch (error) {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        activeObjectUrl.current = null;
      }

      const message = error instanceof AppError ? error.message : "Alterly could not import this media file.";
      setState({ status: "error", asset: null, error: message });
    }
  }, [releaseObjectUrl]);

  const clear = useCallback(() => {
    releaseObjectUrl();
    setState(INITIAL_STATE);
  }, [releaseObjectUrl]);

  return { state, importFile, clear };
}
